import type { LandingContent, Locale } from "./types";

export const landingByLocale: Record<Locale, LandingContent> = {
  en: {
    nav: {
      privacy: "Privacy",
      support: "Support",
      terms: "Terms",
      language: {
        en: "English",
        zh: "简体中文",
        "zh-Hant": "繁體中文"
      },
      languageLabel: {
        en: "Language",
        zh: "选择语言",
        "zh-Hant": "選擇語言"
      },
      navLabel: "Primary navigation"
    },
    hero: {
      title: "Snap a photo, hear the mood.",
      description:
        "Your surroundings often reflect how you feel. Point your camera at whatever is around you, and Moodshot reads the scene, the tone, and the hour on your device. Every photo becomes a spinning vinyl sleeve with a matching Apple Music playlist.",
      cta: "Download on the App Store",
      ctaUnavailable: "Coming soon to the App Store",
      note: "iPhone & iPad · iOS 17 or later · No account · Free download, optional in-app purchase"
    },
    features: [
      {
        title: "Turn surroundings into music",
        description:
          "Scene recognition, overall tone (warmth, brightness, saturation), and the time of day rank eighteen moods to match your space. Recognition runs entirely on your device.",
        icon: "scan"
      },
      {
        title: "Shows its reasoning",
        description:
          "A line under the mood name lists what it saw: the recognized scene, the hour, and the cast of light. Clear, open, and never arbitrary.",
        icon: "eye"
      },
      {
        title: "Plays inside the app",
        description:
          "An Apple Music playlist starts on its own. Full tracks with an Apple Music subscription; 30-second previews without one.",
        icon: "play"
      },
      {
        title: "Try another pressing",
        description:
          "The two runner-up moods sit under OTHER PRESSINGS. Tap one and the sleeve swaps out, the label changes colour, and a different playlist loads.",
        icon: "shuffle"
      },
      {
        title: "Keep the ones worth keeping",
        description:
          "Save a moment, and the photo, mood, and playlist go onto a shelf of record sleeves. Open one to hear it again.",
        icon: "heart"
      },
      {
        title: "No account, genuinely private",
        description:
          "No account, no analytics, no third-party SDKs. Recognition runs entirely on device and works offline; only fetching the playlist needs a connection.",
        icon: "lock"
      }
    ],
    showcase: {
      title: "From a quiet corner to a spinning record",
      description:
        "Three simple steps: point your camera, let the environment pick the record, and listen.",
      steps: [
        {
          title: "Frame what is in front of you",
          description: "A square frame on paper-coloured ground. Tap the vinyl-shaped shutter.",
          preview: "shoot"
        },
        {
          title: "The scene picks the record",
          description:
            "Your photo becomes the sleeve, the vinyl slides out and spins, and the music begins to play.",
          preview: "record"
        },
        {
          title: "A shelf of your own moments",
          description: "Saved moments line up as record sleeves. Open one and it plays again.",
          preview: "shelf"
        }
      ]
    },
    footer: {
      tagline: "Snap a photo, hear the mood.",
      copyright: "© 2026",
      axqora: "Axqora",
      axqoraHref: "https://axqora.com/",
      icp: "粤ICP备2021054614号",
      icpHref: "https://beian.miit.gov.cn/",
      contact: "Contact",
      contactHref: "mailto:axqora@gmail.com",
      footerNavLabel: "Footer navigation"
    }
  },
  zh: {
    nav: {
      privacy: "隐私政策",
      support: "用户支持",
      terms: "用户协议",
      language: {
        en: "English",
        zh: "简体中文",
        "zh-Hant": "繁體中文"
      },
      languageLabel: {
        en: "Language",
        zh: "选择语言",
        "zh-Hant": "選擇語言"
      },
      navLabel: "主导航"
    },
    hero: {
      title: "拍下所见，听懂此刻的心情",
      description:
        "人当下的心情，往往就映在眼前的环境里。手边的咖啡、窗外的天色、深夜亮着的一盏暖灯。掏出手机拍下眼前的一角，让周遭的光影为你转动一张对味的黑胶唱片。识别完全在本地完成，不必联网。",
      cta: "在 App Store 下载",
      ctaUnavailable: "即将上架 App Store",
      note: "iPhone 与 iPad · 需要 iOS 17 或更新版本 · 无需注册 · 免费下载，含可选内购"
    },
    features: [
      {
        title: "让眼前光影变成唱片",
        description: "镜头对准身边的角落。场景、光线冷暖与当前时段在本地实时分析，为你匹配 18 种黑胶唱片中最对味的那一张。识别全程不必联网。",
        icon: "scan"
      },
      {
        title: "选歌依据写在明处",
        description: "唱片下方有一行清晰的小字：认出的场景、拍摄时段与光线冷暖。为什么为你放这张唱片，全摆在眼前，绝非随机乱放。",
        icon: "eye"
      },
      {
        title: "拍完即播，音乐响起",
        description: "无需跳转，匹配的 Apple Music 歌单当场自动开播。有订阅可听完整曲目，未订阅也能直接试听 30 秒官方片段。",
        icon: "play"
      },
      {
        title: "换一版压制",
        description: "排在第二、第三位的候补氛围列在 OTHER PRESSINGS。轻点即可切换，唱片封套、标签色彩与对应歌单同步变换。",
        icon: "shuffle"
      },
      {
        title: "喜欢的瞬间，收进唱片架",
        description: "轻点爱心，当时的画面、氛围与歌单便一同收进你的唱片架，随时翻出来重温。免费版可存 12 张唱片，亦可解锁无限收藏。",
        icon: "heart"
      },
      {
        title: "无需账号，纯粹私密",
        description: "没有账号系统，不接入任何第三方统计或广告 SDK。分析全程在本地完成，离线亦可识别，仅检索歌单需要网络连接。",
        icon: "lock"
      }
    ],
    showcase: {
      title: "把眼前的一角，变成黑胶唱片",
      description: "三步之间，周遭的光影与环境，自然化为流淌的音乐。",
      steps: [
        {
          title: "对准眼前，按下快门",
          description: "纸底上极简的正方形取景框，轻按黑胶快门。",
          preview: "shoot"
        },
        {
          title: "光影转动成唱片",
          description: "拍下的照片变成黑胶封套，黑胶缓缓滑出旋转，契合此刻的歌单随即响起。",
          preview: "record"
        },
        {
          title: "心动瞬间，收进唱片架",
          description: "珍藏的瞬间一张张立在木架上，随时翻出来重温。",
          preview: "shelf"
        }
      ]
    },
    footer: {
      tagline: "拍下所见，听懂此刻的心情。",
      copyright: "© 2026",
      axqora: "Axqora",
      axqoraHref: "https://axqora.com/",
      icp: "粤ICP备2021054614号",
      icpHref: "https://beian.miit.gov.cn/",
      contact: "联系我们",
      contactHref: "mailto:axqora@gmail.com",
      footerNavLabel: "页脚导航"
    }
  },
  "zh-Hant": {
    nav: {
      privacy: "隱私權政策",
      support: "支援",
      terms: "使用條款",
      language: {
        en: "English",
        zh: "简体中文",
        "zh-Hant": "繁體中文"
      },
      languageLabel: {
        en: "Language",
        zh: "选择语言",
        "zh-Hant": "選擇語言"
      },
      navLabel: "主導覽"
    },
    hero: {
      title: "拍一張照片，讀懂這一刻的心情",
      description:
        "眼前的環境，往往映照著當下的心情。手邊的咖啡、窗外的天色、深夜還亮著的一盞暖燈。拿起手機拍下眼前一角，讓周遭的光影為你轉出一張對味的黑膠唱片。辨識完全在裝置端完成，不必連網。",
      cta: "在 App Store 下載",
      ctaUnavailable: "即將上架 App Store",
      note: "iPhone 與 iPad · 需要 iOS 17 或更新版本 · 無需註冊 · 免費下載，含可選 App 內購買"
    },
    features: [
      {
        title: "把眼前光影變成唱片",
        description: "鏡頭對準身邊的角落。場景、光線冷暖與目前時段在裝置端即時分析，為你配對 18 種黑膠唱片中最對味的一張。辨識全程不必連網。",
        icon: "scan"
      },
      {
        title: "選歌依據寫在明處",
        description: "唱片下方有一行清楚的小字：辨識出的場景、拍攝時段與光線冷暖。為什麼放這張唱片，全都攤在眼前，絕非隨機亂放。",
        icon: "eye"
      },
      {
        title: "拍完即播，音樂隨即響起",
        description: "不必跳轉，配對到的 Apple Music 播放列表當場自動開播。已訂閱可聽完整曲目，未訂閱也能直接試聽 30 秒官方片段。",
        icon: "play"
      },
      {
        title: "換一版壓製",
        description: "排在第二、第三位的候補氛圍列在 OTHER PRESSINGS。輕點即可切換，唱片封套、標籤色彩與對應播放列表同步變換。",
        icon: "shuffle"
      },
      {
        title: "喜歡的瞬間，收進唱片架",
        description: "輕點愛心，當下的畫面、氛圍與播放列表便一同收進你的唱片架，隨時翻出來回味。免費版可存 12 張唱片，亦可解鎖無限收藏。",
        icon: "heart"
      },
      {
        title: "無需帳號，純粹私密",
        description: "沒有帳號系統，不介接任何第三方統計或廣告 SDK。分析全程在裝置端完成，離線也能辨識，只有檢索播放列表需要網路連線。",
        icon: "lock"
      }
    ],
    showcase: {
      title: "把眼前一角，變成一張黑膠唱片",
      description: "三個步驟之間，周遭的光影與環境，自然化成流動的音樂。",
      steps: [
        {
          title: "對準眼前，按下快門",
          description: "紙面底色上的極簡方形取景框，輕按黑膠快門。",
          preview: "shoot"
        },
        {
          title: "光影轉動成唱片",
          description: "拍下的照片變成黑膠封套，黑膠緩緩滑出旋轉，契合此刻的播放列表隨即響起。",
          preview: "record"
        },
        {
          title: "心動瞬間，收進唱片架",
          description: "珍藏的瞬間一張張立在木架上，隨時翻出來回味。",
          preview: "shelf"
        }
      ]
    },
    footer: {
      tagline: "拍一張照片，讀懂這一刻的心情。",
      copyright: "© 2026",
      axqora: "Axqora",
      axqoraHref: "https://axqora.com/",
      icp: "粤ICP备2021054614号",
      icpHref: "https://beian.miit.gov.cn/",
      contact: "聯絡我們",
      contactHref: "mailto:axqora@gmail.com",
      footerNavLabel: "頁尾導覽"
    }
  }
};
